import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import {
  addIdToSegmentationValueData,
  type RevenueInsightSegmentationValue,
} from 'lumen/utils/revenue-insight/model-utils';
import { v4 as uuidv4 } from 'uuid';

type RevenueInsightBreakdownsRequestResponse = {
  booking_lead_time: RevenueInsightSegmentationValue[];
  cancellation_lead_time: RevenueInsightSegmentationValue[];
  company: RevenueInsightSegmentationValue[];
  guest_country: RevenueInsightSegmentationValue[];
  los: RevenueInsightSegmentationValue[];
  origin: RevenueInsightSegmentationValue[];
  rate_code: RevenueInsightSegmentationValue[];
  room_type: RevenueInsightSegmentationValue[];
  segment: RevenueInsightSegmentationValue[];
  source: RevenueInsightSegmentationValue[];
  source_profile: RevenueInsightSegmentationValue[];
  travel_agent: RevenueInsightSegmentationValue[];
  weekday: RevenueInsightSegmentationValue[];
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class RevenueInsightBreakdownsRequestSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    booking_lead_time: { embedded: 'always' },
    cancellation_lead_time: { embedded: 'always' },
    company: { embedded: 'always' },
    guest_country: { embedded: 'always' },
    los: { embedded: 'always' },
    origin: { embedded: 'always' },
    rate_code: { embedded: 'always' },
    room_type: { embedded: 'always' },
    segment: { embedded: 'always' },
    source: { embedded: 'always' },
    source_profile: { embedded: 'always' },
    travel_agent: { embedded: 'always' },
    weekday: { embedded: 'always' },
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  extractId() {
    return uuidv4();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  normalizeQueryRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: RevenueInsightBreakdownsRequestResponse,
    id: string | number,
    requestType: string,
  ) {
    // Add `id` to each segmentation-value's segmentation-data object.
    // Necessary because backend does not provide an `id` for these objects.
    // Can't use extractId in revenue-insight/segmentation-data as we want existing store instances
    // to be overwritten when e.g. a new filter is applied (should result in same id).
    Object.values(payload).forEach((segmentationValues) => {
      segmentationValues.forEach((segmentationValue) => {
        addIdToSegmentationValueData(segmentationValue);
      });
    });

    // Wrap the payload in a root key that matches the model name
    const restPayload = { [primaryModelClass.modelName]: payload };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return super.normalizeQueryRecordResponse(
      store,
      primaryModelClass,
      restPayload,
      id,
      requestType,
    );
  }
}
